/**
 * Tableau de bord — refonte 2026-08-28 (design épuré, orientation scolarité
 * + examens). Structure :
 *
 *   1. En-tête sobre (titre + période + actualiser)
 *   2. KPIs clés (grille 4 cartes stat)
 *   3. Ligne "Saisie des notes" — progression devoirs + examens
 *   4. Ligne "Copies & OCR" — cycle complet des examens
 *   5. Répartitions (niveau × pôle) côte-à-côte
 *   6. Alertes système (rôle scolarité)
 *
 * Palette réduite (bleu / vert / ambre / rouge), lignes fines, moins de
 * couleurs saturées, plus d'espace blanc.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Users, BookOpen, FileText, GraduationCap, AlertCircle, CheckCircle,
  Activity, RefreshCw, TrendingUp, ClipboardList, ScanLine, UserCheck,
  Layers, Building2, ShieldAlert, ArrowRight,
} from 'lucide-react';
import { Spinner } from '../components/ui/Spinner';
import { Cycle, SelecteurCycle } from '../components/ui/SelecteurCycle';
import { statistiquesService } from '../services/api.service';
import { StatistiquesDashboard } from '../types';
import AlertesNotesSection from '../components/Dashboard/AlertesNotesSection';
import MatieresCritiquesSection from '../components/Dashboard/MatieresCritiquesSection';

// ─── COMPOSANTS DE BASE ─────────────────────────────────────────────────────

interface KpiProps {
  label: string;
  value: string | number;
  hint?: string;
  icon: React.ReactNode;
  tone?: 'neutral' | 'primary' | 'success' | 'warning' | 'danger';
  trend?: string;
}

const Kpi: React.FC<KpiProps> = ({ label, value, hint, icon, tone = 'neutral', trend }) => {
  const toneRing = {
    neutral: 'border-gray-200 dark:border-gray-700',
    primary: 'border-blue-200 dark:border-blue-800',
    success: 'border-green-200 dark:border-green-800',
    warning: 'border-amber-200 dark:border-amber-800',
    danger:  'border-red-200 dark:border-red-800',
  }[tone];
  const iconBg = {
    neutral: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    primary: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    success: 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    warning: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    danger:  'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  }[tone];
  return (
    <div className={`bg-white dark:bg-gray-900 rounded-xl border ${toneRing} p-4 flex items-start justify-between gap-3 transition-colors hover:border-gray-300 dark:hover:border-gray-600`}>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">{label}</p>
        <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums truncate">{value}</p>
        {hint && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate">{hint}</p>}
        {trend && <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium">{trend}</p>}
      </div>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        {icon}
      </div>
    </div>
  );
};

const Section: React.FC<{
  title: string;
  hint?: string;
  icon: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, hint, icon, action, children }) => (
  <section className="space-y-3">
    <div className="flex items-end justify-between gap-3">
      <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
        <span className="text-gray-400">{icon}</span>
        <h2 className="text-sm font-semibold uppercase tracking-wide">{title}</h2>
        {hint && <span className="text-xs text-gray-400 font-normal normal-case">· {hint}</span>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 ${className}`}>
    {children}
  </div>
);

// Barre de progression épurée
const Progress: React.FC<{ value: number; max: number; tone?: 'primary' | 'success' | 'warning' }> = ({
  value, max, tone = 'primary',
}) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const color = { primary: 'bg-blue-500', success: 'bg-green-500', warning: 'bg-amber-500' }[tone];
  return (
    <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-1.5 overflow-hidden">
      <div className={`${color} h-1.5 rounded-full transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
};

// ─── DASHBOARD PRINCIPAL ────────────────────────────────────────────────────

export const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<StatistiquesDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Licence et master ne se mélangent pas dans les statistiques (2026-10-02).
  const [cycle, setCycle] = useState<Cycle>('licence');

  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await statistiquesService.getDashboard({ cycle });
      setStats(data);
    } catch (err: any) {
      console.error('Erreur chargement statistiques:', err);
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [cycle]);

  useEffect(() => { fetchDashboardStats(); }, [fetchDashboardStats]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Spinner size="lg" />
        <p className="mt-4 text-gray-500 dark:text-gray-400 text-sm">Chargement…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle size={40} className="text-red-500 mb-3" />
        <p className="text-red-600 dark:text-red-400 font-medium">Erreur de chargement</p>
        <p className="text-gray-500 dark:text-gray-400 mt-1 text-xs">{error}</p>
        <button onClick={fetchDashboardStats}
          className="mt-4 px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm">
          <RefreshCw size={14} /> Réessayer
        </button>
      </div>
    );
  }
  if (!stats) return null;

  // ── Dérivés ────────────────────────────────────────────────────────────
  const totalCopies = stats.copies?.total_copies || 0;
  const copiesAssociees = stats.copies?.copies_associees || 0;
  const tauxAssociation = totalCopies > 0 ? Math.round((copiesAssociees / totalCopies) * 100) : 0;
  const ocrTraites = stats.ocr?.total_traites || 0;
  const ocrAttente = stats.ocr?.en_attente || 0;

  const totalEval = (stats.valides || 0) + (stats.recales || 0);
  const pctValides = totalEval > 0 ? Math.round(((stats.valides || 0) / totalEval) * 100) : 0;

  return (
    <div className="max-w-screen-2xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Tableau de bord</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-2">
            <span>Institut International 2i</span>
            {stats.annee_academique && (
              <>
                <span className="text-gray-300 dark:text-gray-700">·</span>
                <span>Année <span className="font-medium text-gray-700 dark:text-gray-300">{stats.annee_academique}</span></span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <SelecteurCycle value={cycle} onChange={setCycle} taille="sm" />
          <span className="ml-2">Dernière maj : {new Date().toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</span>
          <button onClick={fetchDashboardStats}
            className="ml-2 flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
            <RefreshCw size={12} /> Actualiser
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* KPIs CLÉS */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi
          label="Étudiants actifs"
          value={(stats.etudiants_actifs || 0).toLocaleString('fr-FR')}
          hint={`sur ${(stats.total_etudiants || 0).toLocaleString('fr-FR')} inscrits`}
          icon={<Users size={18} />}
          tone="primary"
        />
        <Kpi
          label="Notes saisies"
          value={((stats.total_notes_devoirs || 0) + (stats.total_notes_examens || 0)).toLocaleString('fr-FR')}
          hint={`${(stats.total_notes_devoirs || 0).toLocaleString('fr-FR')} devoirs · ${(stats.total_notes_examens || 0).toLocaleString('fr-FR')} examens`}
          icon={<ClipboardList size={18} />}
          tone="neutral"
        />
        <Kpi
          label="Taux de réussite global"
          value={`${stats.taux_reussite ?? 0}%`}
          hint={`${(stats.valides || 0)} validés · ${(stats.recales || 0)} recalés · détail ci-dessous`}
          icon={<TrendingUp size={18} />}
          tone={stats.taux_reussite >= 60 ? 'success' : stats.taux_reussite >= 40 ? 'warning' : 'danger'}
        />
        <Kpi
          label="Moyenne générale"
          value={`${stats.moyenne_generale ?? 0}/20`}
          hint={stats.absents ? `${stats.absents} absences` : undefined}
          icon={<GraduationCap size={18} />}
          tone={stats.moyenne_generale >= 12 ? 'success' : stats.moyenne_generale >= 10 ? 'warning' : 'danger'}
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SCOLARITÉ — Saisie des notes                                        */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Section
        title="Scolarité"
        hint="Saisie et suivi des notes"
        icon={<ClipboardList size={16} />}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Progression saisie devoirs */}
          <Card className="lg:col-span-2">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
              Progression de la saisie
            </div>
            {stats.progression_saisie ? (
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-baseline mb-1.5">
                    <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-blue-500" />
                      Devoirs
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {stats.progression_saisie.devoirs.saisies.toLocaleString('fr-FR')}
                      </span>
                      {' / '}
                      {stats.progression_saisie.devoirs.attendues.toLocaleString('fr-FR')}
                      <span className="ml-2 font-semibold text-blue-600 dark:text-blue-400">
                        {stats.progression_saisie.devoirs.pourcentage}%
                      </span>
                    </span>
                  </div>
                  <Progress
                    value={stats.progression_saisie.devoirs.saisies}
                    max={stats.progression_saisie.devoirs.attendues}
                    tone="primary"
                  />
                </div>
                <div>
                  <div className="flex justify-between items-baseline mb-1.5">
                    <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-green-500" />
                      Examens
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {stats.progression_saisie.examens.saisies.toLocaleString('fr-FR')}
                      </span>
                      {' / '}
                      {stats.progression_saisie.examens.attendues.toLocaleString('fr-FR')}
                      <span className="ml-2 font-semibold text-green-600 dark:text-green-400">
                        {stats.progression_saisie.examens.pourcentage}%
                      </span>
                    </span>
                  </div>
                  <Progress
                    value={stats.progression_saisie.examens.saisies}
                    max={stats.progression_saisie.examens.attendues}
                    tone="success"
                  />
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400">Aucune donnée de progression.</p>
            )}
          </Card>

          {/* Résultats académiques compact */}
          <Card>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
              Résultats globaux
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between py-1">
                <span className="text-gray-600 dark:text-gray-400 flex items-center gap-2">
                  <CheckCircle size={14} className="text-green-500" /> Validés
                </span>
                <span className="tabular-nums font-semibold text-gray-900 dark:text-gray-100">
                  {(stats.valides || 0).toLocaleString('fr-FR')} <span className="text-xs text-gray-400 font-normal">({pctValides}%)</span>
                </span>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-gray-600 dark:text-gray-400 flex items-center gap-2">
                  <span className="h-3.5 w-3.5 rounded-full bg-red-500/20 border border-red-500 flex-shrink-0" />
                  Recalés
                </span>
                <span className="tabular-nums font-semibold text-gray-900 dark:text-gray-100">
                  {(stats.recales || 0).toLocaleString('fr-FR')}
                </span>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-gray-600 dark:text-gray-400 flex items-center gap-2">
                  <AlertCircle size={14} className="text-amber-500" /> Absents
                </span>
                <span className="tabular-nums font-semibold text-gray-900 dark:text-gray-100">
                  {(stats.absents || 0).toLocaleString('fr-FR')}
                </span>
              </div>
              <div className="pt-2 mt-1 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400">Moyenne classe</span>
                <span className="tabular-nums font-bold text-gray-900 dark:text-gray-100">
                  {stats.moyenne_generale ?? 0}<span className="text-xs text-gray-400">/20</span>
                </span>
              </div>
            </div>
          </Card>
        </div>

        {/* Réussite détaillée par semestre × session (fix 2026-08-28) */}
        {stats.reussite_detail && (Object.values(stats.reussite_detail).some(d => d.evalues > 0)) && (
          <Card>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
              <TrendingUp size={14} className="text-gray-400" />
              Réussite par semestre × session
              <span className="text-xs font-normal text-gray-400">(ordinaire vs rattrapage)</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(['s1', 's2'] as const).map(semKey => {
                const d = stats.reussite_detail?.[semKey];
                if (!d || d.attendus === 0) return null;
                const semLabel = semKey === 's1' ? 'Semestre 1 (impair)' : 'Semestre 2 (pair)';
                const incomplete = d.saisie_incomplete;
                return (
                  <div key={semKey} className={`border rounded-lg p-3 ${
                    incomplete
                      ? 'border-amber-300 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">{semLabel}</div>
                      <div className="flex items-center gap-2 text-xs tabular-nums">
                        <span className="text-gray-500">
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{d.evalues}</span>
                          {'/'}{d.attendus} traités
                        </span>
                        {incomplete && (
                          <span className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 font-semibold uppercase text-[10px]">
                            Saisie en cours
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Couverture (progression traitement) — quand incomplète */}
                    {incomplete && (
                      <div className="mb-2 -mt-1">
                        <div className="flex justify-between items-baseline mb-1">
                          <span className="text-[11px] text-amber-700 dark:text-amber-300 flex items-center gap-1.5 font-medium">
                            📋 Couverture saisie
                          </span>
                          <span className="text-[11px] tabular-nums text-amber-700 dark:text-amber-300">
                            {d.non_traites} en attente · <span className="font-semibold">{d.couverture_pct}%</span>
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1 overflow-hidden">
                          <div className="bg-amber-500 h-1 rounded-full transition-all" style={{ width: `${d.couverture_pct}%` }} />
                        </div>
                      </div>
                    )}
                    {/* Session ordinaire */}
                    <div className="mb-2">
                      <div className="flex justify-between items-baseline mb-1">
                        <span className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full bg-blue-500" />
                          Session ordinaire
                        </span>
                        <span className="text-xs tabular-nums text-gray-500">
                          <span className="font-semibold text-blue-600 dark:text-blue-400">{d.ord_valides}</span>
                          {' validés · '}
                          <span className="font-semibold text-blue-600 dark:text-blue-400">{d.ord_taux}%</span>
                        </span>
                      </div>
                      <Progress value={d.ord_valides} max={d.attendus} tone="primary" />
                    </div>
                    {/* Session rattrapage */}
                    <div className="mb-2">
                      <div className="flex justify-between items-baseline mb-1">
                        <span className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full bg-amber-500" />
                          Session rattrapage
                        </span>
                        <span className="text-xs tabular-nums text-gray-500">
                          {d.ratt_participants > 0 ? (
                            <>
                              <span className="font-semibold text-amber-600 dark:text-amber-400">{d.ratt_valides}</span>
                              {'/'}{d.ratt_participants}
                              {' rattrapés · '}
                              <span className="font-semibold text-amber-600 dark:text-amber-400">{d.ratt_taux}%</span>
                            </>
                          ) : (
                            <span className="italic text-gray-400">aucun passage</span>
                          )}
                        </span>
                      </div>
                      <Progress
                        value={d.ratt_valides}
                        max={Math.max(1, d.ratt_participants)}
                        tone="warning"
                      />
                    </div>
                    {/* Bilan final */}
                    <div className="pt-2 mt-1 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-xs">
                      <span className="text-gray-600 dark:text-gray-400">
                        Bilan {incomplete ? 'provisoire' : 'final'} :{' '}
                        <span className="text-green-700 dark:text-green-400 font-semibold">{d.valides_total} validés</span>
                        {d.recales_definitifs > 0 && (
                          <> · <span className="text-red-700 dark:text-red-400 font-semibold">{d.recales_definitifs} recalés</span></>
                        )}
                        {incomplete && (
                          <> · <span className="text-amber-700 dark:text-amber-400 font-semibold">{d.non_traites} en attente</span></>
                        )}
                      </span>
                      <span className={`text-sm font-bold tabular-nums ${
                        incomplete
                          ? 'text-amber-700 dark:text-amber-400'
                          : d.taux_final >= 70 ? 'text-green-600 dark:text-green-400'
                          : d.taux_final >= 50 ? 'text-amber-600 dark:text-amber-400'
                          : 'text-red-600 dark:text-red-400'
                      }`} title={incomplete
                          ? `Taux calculé sur les ${d.attendus} inscrits (saisie encore incomplète)`
                          : 'Taux définitif'}>
                        {d.taux_final}%
                        {incomplete && <span className="text-[10px] font-normal ml-0.5">*</span>}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Note bas de section */}
            {Object.values(stats.reussite_detail || {}).some(d => d.saisie_incomplete) && (
              <p className="mt-3 text-[11px] text-amber-700 dark:text-amber-400 italic flex items-start gap-1">
                <span>*</span>
                <span>
                  Les taux marqués d'un astérisque sont provisoires : la saisie des notes n'est pas terminée.
                  Le dénominateur reste les <strong>{Object.values(stats.reussite_detail || {})[0]?.attendus || 0} inscrits</strong> —
                  les étudiants non encore traités comptent comme « en attente », pas comme échec.
                </span>
              </p>
            )}
          </Card>
        )}
      </Section>

      {/* Alertes notes trans-classes */}
      <Section
        title="Anomalies notes"
        hint="Écarts, incohérences, retards"
        icon={<ShieldAlert size={16} />}
      >
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
          <AlertesNotesSection
            anneeAcademique={stats?.annee_academique}
            pole={(stats as any)?.pole_filtre || undefined}
          />
        </div>
      </Section>

      {/* Fix 2026-09-02 : matières critiques (aide décision pédagogique) */}
      <Section
        title="Difficultés pédagogiques"
        hint="Où les étudiants ont mal travaillé (par semestre et session)"
        icon={<TrendingUp size={16} />}
      >
        <MatieresCritiquesSection
          anneeAcademique={stats?.annee_academique}
          pole={(stats as any)?.pole_filtre || undefined}
        />
      </Section>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* EXAMENS — Copies & OCR                                              */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Section
        title="Examens"
        hint="Cycle copies · association · OCR"
        icon={<ScanLine size={16} />}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Copies */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                Copies d'examen
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                {tauxAssociation}% associées
              </span>
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 tabular-nums mb-2">
              {totalCopies.toLocaleString('fr-FR')}
            </div>
            <Progress value={copiesAssociees} max={totalCopies || 1} tone="primary" />
            <div className="flex justify-between text-xs mt-2 text-gray-500 dark:text-gray-400">
              <span>Associées <span className="font-semibold text-green-600 dark:text-green-400">{copiesAssociees}</span></span>
              <span>Restant <span className="font-semibold text-amber-600 dark:text-amber-400">{(stats.copies?.copies_non_associees || 0)}</span></span>
            </div>
          </Card>

          {/* OCR relevés */}
          <Card>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
              Relevés OCR
            </div>
            <div className="grid grid-cols-2 gap-2">
              <MiniStat label="Devoirs" value={stats.ocr?.releves_devoirs || 0} />
              <MiniStat label="Examens" value={stats.ocr?.releves_examens || 0} />
              <MiniStat label="Traités" value={ocrTraites} tone="success" />
              <MiniStat label="En attente" value={ocrAttente} tone={ocrAttente > 0 ? 'warning' : 'neutral'} />
            </div>
          </Card>

          {/* Enveloppe pédagogique */}
          <Card>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
              Cadre pédagogique
            </div>
            <div className="space-y-2 text-sm">
              <RowStat icon={<BookOpen size={14} />} label="Unités d'enseignement" value={stats.total_ue || 0} />
              <RowStat icon={<FileText size={14} />} label="Matières" value={stats.total_matieres || 0} />
              <RowStat icon={<GraduationCap size={14} />} label="Enseignants" value={stats.total_enseignants || 0} />
            </div>
          </Card>
        </div>
      </Section>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* RÉPARTITIONS — Niveau + Pôle                                        */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Section
        title="Répartitions"
        hint="Effectifs par niveau et par pôle"
        icon={<Layers size={16} />}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Niveau */}
          <Card>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
              <Layers size={14} className="text-gray-400" /> Effectifs par niveau
            </div>
            <div className="space-y-1.5">
              {stats.stats_niveau && Object.entries(stats.stats_niveau).length > 0
                ? Object.entries(stats.stats_niveau).map(([niveau, stat]) => (
                    <div key={niveau} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800/40">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="inline-flex items-center justify-center h-6 w-8 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-bold">
                          {niveau}
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm text-gray-800 dark:text-gray-200 tabular-nums">
                            {(stat.total || 0).toLocaleString('fr-FR')} <span className="text-xs text-gray-400 font-normal">étud.</span>
                          </div>
                          {(stat.cours_du_soir != null && stat.cours_du_soir > 0) && (
                            <div className="text-[10px] text-gray-500 dark:text-gray-400">
                              FI {stat.formation_initiale ?? 0} · CS {stat.cours_du_soir}
                              {stat.double_diplome ? ` · DD ${stat.double_diplome}` : ''}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={`text-sm font-semibold tabular-nums ${
                        (stat.taux_reussite ?? -1) >= 70 ? 'text-green-600 dark:text-green-400'
                        : (stat.taux_reussite ?? -1) >= 50 ? 'text-amber-600 dark:text-amber-400'
                        : (stat.taux_reussite ?? -1) > 0 ? 'text-red-600 dark:text-red-400'
                        : 'text-gray-400 dark:text-gray-500'
                      }`}>
                        {stat.taux_reussite != null && stat.taux_reussite >= 0 ? `${stat.taux_reussite}%` : '—'}
                      </div>
                    </div>
                  ))
                : <p className="text-sm text-gray-400 text-center py-4">Aucune donnée</p>}
            </div>
          </Card>

          {/* Pôle */}
          <Card>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
              <Building2 size={14} className="text-gray-400" /> Effectifs par pôle
            </div>
            <div className="space-y-1.5">
              {stats.stats_pole && Object.entries(stats.stats_pole).length > 0
                ? Object.entries(stats.stats_pole).map(([pole, stat]) => (
                    <div key={pole} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800/40">
                      <div className="min-w-0">
                        <div className="text-sm text-gray-800 dark:text-gray-200 truncate">{pole}</div>
                        <div className="text-[10px] text-gray-500 dark:text-gray-400 tabular-nums">
                          {(stat.total || 0).toLocaleString('fr-FR')} étud. · {(stat.actifs || 0)} actifs
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-blue-600 dark:text-blue-400 tabular-nums">
                          {stat.part_marche}%
                        </div>
                        {stat.taux_reussite > 0 && (
                          <div className="text-[10px] text-gray-500 dark:text-gray-400 tabular-nums">
                            {stat.taux_reussite}% réussite
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                : <p className="text-sm text-gray-400 text-center py-4">Aucune donnée</p>}
            </div>
          </Card>
        </div>

        {/* Répartition par type de formation (uniquement si CS ou DD > 0) */}
        {((stats.effectif_cours_du_soir ?? 0) > 0 || (stats.effectif_double_diplome ?? 0) > 0) && (
          <Card>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
              Répartition par type de formation
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <TypeFormation
                icon={<UserCheck size={16} />}
                label="Formation Initiale"
                value={stats.effectif_formation_initiale ?? 0}
                tone="primary"
              />
              {(stats.effectif_cours_du_soir ?? 0) > 0 && (
                <TypeFormation
                  icon={<Users size={16} />}
                  label="Cours du Soir"
                  value={stats.effectif_cours_du_soir!}
                  tone="warning"
                />
              )}
              {(stats.effectif_double_diplome ?? 0) > 0 && (
                <TypeFormation
                  icon={<GraduationCap size={16} />}
                  label="Double Diplôme"
                  value={stats.effectif_double_diplome!}
                  tone="neutral"
                  hint="inclus dans FI"
                />
              )}
            </div>
          </Card>
        )}
      </Section>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* ALERTES SYSTÈME                                                     */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Section
        title="Alertes système"
        hint="Notifications importantes"
        icon={<Activity size={16} />}
      >
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
          {stats.alertes && stats.alertes.length > 0 ? (
            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
              {stats.alertes.map((alerte, idx) => {
                const tone = {
                  error:   'text-red-600 dark:text-red-400',
                  warning: 'text-amber-600 dark:text-amber-400',
                  success: 'text-green-600 dark:text-green-400',
                  info:    'text-blue-600 dark:text-blue-400',
                }[alerte.type] || 'text-gray-500';
                return (
                  <li key={idx} className="py-2.5 flex items-start gap-3">
                    <AlertCircle className={`${tone} flex-shrink-0 mt-0.5`} size={16} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{alerte.titre}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{alerte.message}</p>
                    </div>
                    <ArrowRight size={14} className="text-gray-300 dark:text-gray-600 flex-shrink-0 mt-0.5" />
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="text-center py-6">
              <CheckCircle className="mx-auto text-green-500 mb-2" size={32} />
              <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">Aucune alerte</p>
              <p className="text-xs text-gray-400 mt-0.5">Tous les systèmes fonctionnent normalement</p>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
};

// ─── Petits composants utilitaires ─────────────────────────────────────────

const MiniStat: React.FC<{ label: string; value: number; tone?: 'neutral' | 'success' | 'warning' }> = ({
  label, value, tone = 'neutral',
}) => {
  const color = {
    neutral: 'text-gray-800 dark:text-gray-200',
    success: 'text-green-700 dark:text-green-400',
    warning: 'text-amber-700 dark:text-amber-400',
  }[tone];
  const bg = {
    neutral: 'bg-gray-50 dark:bg-gray-800/50',
    success: 'bg-green-50 dark:bg-green-900/20',
    warning: 'bg-amber-50 dark:bg-amber-900/20',
  }[tone];
  return (
    <div className={`${bg} rounded-lg p-2.5`}>
      <div className={`text-xl font-bold tabular-nums ${color}`}>{value.toLocaleString('fr-FR')}</div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mt-0.5">{label}</div>
    </div>
  );
};

const RowStat: React.FC<{ icon: React.ReactNode; label: string; value: number }> = ({ icon, label, value }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-gray-600 dark:text-gray-400 flex items-center gap-2">
      <span className="text-gray-400">{icon}</span> {label}
    </span>
    <span className="tabular-nums font-semibold text-gray-900 dark:text-gray-100">
      {value.toLocaleString('fr-FR')}
    </span>
  </div>
);

const TypeFormation: React.FC<{
  icon: React.ReactNode; label: string; value: number;
  tone?: 'primary' | 'warning' | 'neutral'; hint?: string;
}> = ({ icon, label, value, tone = 'neutral', hint }) => {
  const bg = {
    primary: 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-900',
    warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-900',
    neutral: 'bg-gray-50 dark:bg-gray-800/50 border-gray-100 dark:border-gray-800',
  }[tone];
  const color = {
    primary: 'text-blue-700 dark:text-blue-300',
    warning: 'text-amber-700 dark:text-amber-300',
    neutral: 'text-gray-700 dark:text-gray-300',
  }[tone];
  return (
    <div className={`${bg} border rounded-lg p-3 flex items-center gap-3`}>
      <span className={color}>{icon}</span>
      <div className="min-w-0 flex-1">
        <div className={`text-lg font-bold tabular-nums ${color}`}>{value.toLocaleString('fr-FR')}</div>
        <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
          {label}
          {hint && <span className="text-[10px] text-gray-400 ml-1">({hint})</span>}
        </div>
      </div>
    </div>
  );
};
